import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import RichTextEditor, { type RichTextEditorHandle } from '../ui/rich-text-editor';
import NachrichtVorschau from './NachrichtVorschau';
import { cn } from '../../lib/utils';

import {
  communicationsApi,
  emailTemplatesApi,
  smsTemplatesApi,
  templateVariablesApi,
  type TemplateVariableMeta,
  type CommunicationsRecipient,
} from '../../lib/api';

// ---------------------------------------------------------------------------
// Shared types (re-exported for consumers)
// ---------------------------------------------------------------------------

export type CommunicationChannel = 'email' | 'sms';

export type CommunicationRecipient =
  | {
      id: string;
      type: 'person';
      wpUserId: number;
      subIndex?: number | null;
      name: string;
      /**
       * Im Formular geänderte Kontaktdaten, die noch nicht gespeichert sind.
       * Ohne sie zeigte die Vorschau die alte Adresse, während der Versand
       * längst die neue nahm (T-0031). Verschickt wird weiterhin an das, was
       * der Server auflöst — das hier ist die Anzeige.
       */
      email?: string | null;
      phone?: string | null;
    }
  | {
      id: string;
      type: 'trial_request';
      trialRequestId: number;
      name: string;
    };

export type EmailDraft = { subjectTpl: string; htmlTpl: string; textTpl?: string };
export type SmsDraft = { smsTpl: string };

// ---------------------------------------------------------------------------
// v2 types – groups-based API
// ---------------------------------------------------------------------------

export type RecipientGroup = {
  /** Unique key for this group, e.g. 'student' | 'teacher' */
  key: string;
  /** Tab label shown when multiple groups exist, e.g. 'Teilnehmer' | 'Lehrer' */
  label: string;
  /** Recipients belonging to this group */
  recipients: CommunicationRecipient[];
  /** Template keys per channel (loaded automatically when active) */
  templateKeys?: { email?: string; sms?: string };
  /** Template variables for placeholder rendering */
  vars?: Record<string, any>;
  /** Default channel selection for recipients in this group */
  defaultSelectedChannels?: { email?: boolean; sms?: boolean };
};

export type GroupDraftState = {
  selectedChannels: Record<CommunicationChannel, boolean>;
  email: EmailDraft;
  sms: SmsDraft;
};

export type CommunicationComposerProps = {
  /** Pass the wizard's `open && step === N` to reset state on open/step change */
  active?: boolean;
  standortId: number;
  /** One or more recipient groups. Multiple groups render as tabs. */
  groups: RecipientGroup[];
  /** Optional prefilled drafts keyed by group key */
  initialGroupDrafts?: Partial<Record<string, Partial<GroupDraftState>>>;
  /** Versandzweck beeinflusst Kanalregeln */
  purpose?: 'transactional' | 'marketing';
  /** Restrict channels when the calling workflow cannot dispatch all providers. */
  allowedChannels?: { email?: boolean; sms?: boolean };
  /** Called whenever drafts or channel selections change in any group */
  onDraftsChange?: (state: { groups: Record<string, GroupDraftState> }) => void;
};

// ---------------------------------------------------------------------------
// Internal per-group state
// ---------------------------------------------------------------------------

type ResolvedRecipient = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  activeChannels: { email: boolean; sms: boolean };
  /** Vom Kunden fürs Office gesperrt — das Backend lässt diese Kanäle aus (T-0081). */
  blockedChannels?: { email: boolean; sms: boolean };
  communicationPolicy?: { transactionalEmailRequired?: boolean; marketingEmailOptIn?: boolean };
};

type PerGroupState = {
  resolved: Record<string, ResolvedRecipient>;
  isResolving: boolean;
  /** Die Empfänger wurden mindestens einmal aufgelöst (erfolgreich oder nicht). */
  hasResolved: boolean;
  selectedByRecipient: Record<string, Record<CommunicationChannel, boolean>>;
  emailDraft: EmailDraft;
  smsDraft: SmsDraft;
  channelTab: CommunicationChannel;
  isLoadingTemplate: boolean;
  allowedVarsMeta: TemplateVariableMeta[];
};

function createEmptyGroupState(): PerGroupState {
  return {
    resolved: {},
    isResolving: false,
    hasResolved: false,
    selectedByRecipient: {},
    emailDraft: { subjectTpl: '', htmlTpl: '', textTpl: '' },
    smsDraft: { smsTpl: '' },
    channelTab: 'email',
    isLoadingTemplate: false,
    allowedVarsMeta: [],
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CommunicationComposer({
  active = true,
  standortId,
  groups,
  initialGroupDrafts,
  purpose = 'transactional',
  allowedChannels = { email: true, sms: true },
  onDraftsChange,
}: Readonly<CommunicationComposerProps>) {
  // Active group tab (by key)
  const [activeGroupKey, setActiveGroupKey] = useState<string>(groups[0]?.key ?? '');

  // Per-group state keyed by group.key
  const [groupStates, setGroupStates] = useState<Record<string, PerGroupState>>({});

  // Per-group editor refs
  const editorRefs = useRef<Record<string, RichTextEditorHandle | null>>({});
  const loadedTemplateKeysRef = useRef<Record<string, { email: string; sms: string }>>({});
  const wasActiveRef = useRef(false);
  const lastInitSignatureRef = useRef('');

  const groupInitSignature = useMemo(
    () =>
      groups
        .map((g) => `${g.key}:${g.recipients.map((r) => r.id).join(',')}`)
        .join('|'),
    [groups],
  );
  const templateSignature = useMemo(
    () =>
      groups
        .map((g) => `${g.key}:${g.templateKeys?.email?.trim() ?? ''}:${g.templateKeys?.sms?.trim() ?? ''}`)
        .join('|'),
    [groups],
  );

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getGS = useCallback(
    (key: string): PerGroupState => groupStates[key] ?? createEmptyGroupState(),
    [groupStates],
  );

  const updateGS = useCallback(
    (key: string, patch: Partial<PerGroupState> | ((prev: PerGroupState) => Partial<PerGroupState>)) => {
      setGroupStates((prev) => {
        const current = prev[key] ?? createEmptyGroupState();
        const updates = typeof patch === 'function' ? patch(current) : patch;
        if (!updates || Object.keys(updates).length === 0) {
          return prev;
        }

        const nextGroup = { ...current, ...updates };
        let changed = false;
        for (const k of Object.keys(updates) as Array<keyof PerGroupState>) {
          if (!Object.is(current[k], nextGroup[k])) {
            changed = true;
            break;
          }
        }
        if (!changed) return prev;

        return { ...prev, [key]: nextGroup };
      });
    },
    [],
  );

  const toBackend = useCallback(
    (recipients: CommunicationRecipient[]): CommunicationsRecipient[] =>
      recipients.map((r) =>
        r.type === 'person'
          ? { type: 'person', wpUserId: r.wpUserId, subIndex: r.subIndex ?? null, clientId: r.id }
          : { type: 'trial_request', trialRequestId: r.trialRequestId, clientId: r.id },
      ),
    [],
  );

  // ---------------------------------------------------------------------------
  // Template & variable loading
  // ---------------------------------------------------------------------------

  const loadTemplate = useCallback(
    async (groupKey: string, ch: CommunicationChannel, templateKeys?: { email?: string; sms?: string }) => {
      const key = (ch === 'email' ? templateKeys?.email : templateKeys?.sms)?.trim() ?? '';
      if (!key) return;
      updateGS(groupKey, { isLoadingTemplate: true });
      try {
        if (ch === 'email') {
          const tpl = await emailTemplatesApi.get(key);
          updateGS(groupKey, {
            emailDraft: {
              subjectTpl: tpl.subject || '',
              htmlTpl: tpl.body_html || '',
              textTpl: tpl.body_text || '',
            },
            isLoadingTemplate: false,
          });
        } else {
          const tpl = await smsTemplatesApi.get(key);
          updateGS(groupKey, {
            smsDraft: { smsTpl: tpl.body || '' },
            isLoadingTemplate: false,
          });
        }
      } catch (error) {
        console.warn('[CommunicationComposer] Template konnte nicht geladen werden', {
          groupKey,
          channel: ch,
          templateKey: key,
          error,
        });
        updateGS(groupKey, { isLoadingTemplate: false });
      }
    },
    [updateGS],
  );

  const loadVarsMeta = useCallback(
    async (groupKey: string, ch: CommunicationChannel, templateKeys?: { email?: string; sms?: string }) => {
      const key = (ch === 'email' ? templateKeys?.email : templateKeys?.sms)?.trim() ?? '';
      if (!key) {
        updateGS(groupKey, { allowedVarsMeta: [] });
        return;
      }
      try {
        const meta = await templateVariablesApi.getMeta(key);
        updateGS(groupKey, { allowedVarsMeta: meta || [] });
      } catch (error) {
        console.warn('[CommunicationComposer] Template-Variablen konnten nicht geladen werden', {
          groupKey,
          channel: ch,
          templateKey: key,
          error,
        });
        updateGS(groupKey, { allowedVarsMeta: [] });
      }
    },
    [updateGS],
  );

  // ---------------------------------------------------------------------------
  // Reset when `active` changes (wizard opens/closes or step changes)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!active) {
      wasActiveRef.current = false;
      return;
    }

    const shouldInit = !wasActiveRef.current || lastInitSignatureRef.current !== groupInitSignature;
    if (!shouldInit) return;
    wasActiveRef.current = true;
    lastInitSignatureRef.current = groupInitSignature;

    // Set active group tab to first group
    setActiveGroupKey(groups[0]?.key ?? '');
    loadedTemplateKeysRef.current = {};

    // Initialize state for each group
    const initial: Record<string, PerGroupState> = {};
    for (const g of groups) {
      const base = createEmptyGroupState();
      const draft = initialGroupDrafts?.[g.key];
      const emailDraft = draft?.email ? { ...base.emailDraft, ...draft.email } : base.emailDraft;
      const smsDraft = draft?.sms ? { ...base.smsDraft, ...draft.sms } : base.smsDraft;
      initial[g.key] = {
        ...base,
        emailDraft,
        smsDraft,
      };
    }
    setGroupStates(initial);
  }, [active, groups, initialGroupDrafts, groupInitSignature]);

  useEffect(() => {
    if (!active) return;

    const seen = new Set<string>();
    for (const g of groups) {
      seen.add(g.key);
      const emailKey = g.templateKeys?.email?.trim() ?? '';
      const smsKey = g.templateKeys?.sms?.trim() ?? '';
      const previous = loadedTemplateKeysRef.current[g.key];

      if (!previous || previous.email !== emailKey) {
        void loadTemplate(g.key, 'email', g.templateKeys);
        void loadVarsMeta(g.key, 'email', g.templateKeys);
      }
      if (!previous || previous.sms !== smsKey) {
        void loadTemplate(g.key, 'sms', g.templateKeys);
      }

      loadedTemplateKeysRef.current[g.key] = { email: emailKey, sms: smsKey };
    }

    for (const existingKey of Object.keys(loadedTemplateKeysRef.current)) {
      if (!seen.has(existingKey)) {
        delete loadedTemplateKeysRef.current[existingKey];
      }
    }
  }, [active, groups, templateSignature, loadTemplate, loadVarsMeta]);

  // ---------------------------------------------------------------------------
  // Resolve recipients for each group
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!active) return;
    if (!standortId || standortId <= 0) return;

    let cancelled = false;

    for (const g of groups) {
      if (!g.recipients.length) continue;
      const backend = toBackend(g.recipients);
      const gKey = g.key;

      const run = async () => {
        updateGS(gKey, { isResolving: true });
        try {
          const res = await communicationsApi.resolveRecipients({ standortId, recipients: backend, purpose });
          const items = Array.isArray(res?.items) ? res.items : [];
          const map: Record<string, ResolvedRecipient> = {};
          for (const it of items) {
            const id = String(it?.id || it?.recipientId || '');
            if (!id) continue;
            const activeChannels = (
              it?.activeChannels && typeof it.activeChannels === 'object'
                ? it.activeChannels
                : {}
            ) as Record<string, unknown>;
            map[id] = {
              id,
              name: String(it?.name || ''),
              email: typeof it?.email === 'string' ? it.email : null,
              phone: typeof it?.phone === 'string' ? it.phone : null,
              activeChannels: {
                email: !!activeChannels.email,
                sms: !!activeChannels.sms,
              },
              blockedChannels: {
                email: Array.isArray(it?.blockedChannels) && it.blockedChannels.includes('email'),
                sms: Array.isArray(it?.blockedChannels) && it.blockedChannels.includes('sms'),
              },
              communicationPolicy: it?.communicationPolicy ?? undefined,
            };
          }
          // Was im Formular steht, gewinnt über den gespeicherten Stand.
          for (const empfaenger of g.recipients) {
            if (empfaenger.type !== 'person') continue;
            const eintrag = map[empfaenger.id];
            if (!eintrag) continue;
            const email = String(empfaenger.email || '').trim();
            const phone = String(empfaenger.phone || '').trim();
            if (email) eintrag.email = email;
            if (phone) eintrag.phone = phone;
          }
          if (!cancelled) updateGS(gKey, { resolved: map, isResolving: false, hasResolved: true });
        } catch {
          if (!cancelled) updateGS(gKey, { resolved: {}, isResolving: false, hasResolved: true });
        }
      };

      void run();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, standortId, purpose, toBackend, updateGS, groupInitSignature]);

  // ---------------------------------------------------------------------------
  // Reconcile per-recipient channel selections after resolve
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!active) return;
    for (const g of groups) {
      if (!g.recipients.length) continue;

      updateGS(g.key, (prev) => {
        const next: Record<string, Record<CommunicationChannel, boolean>> = { ...prev.selectedByRecipient };
        let changed = false;
        const ids = new Set(g.recipients.map((r) => r.id));
        // remove stale
        for (const k of Object.keys(next)) {
          if (!ids.has(k)) {
            delete next[k];
            changed = true;
          }
        }
        for (const r of g.recipients) {
          const rr = prev.resolved[r.id];
          if (!rr) continue;
          const eActive = allowedChannels.email !== false && !!rr.activeChannels?.email;
          const sActive = allowedChannels.sms !== false && !!rr.activeChannels?.sms;
          const marketingOptIn = rr.communicationPolicy?.marketingEmailOptIn ?? true;
          // Werbung nur mit Einwilligung — per E-Mail wie per SMS (T-0178).
          const emailAllowedByPurpose = purpose !== 'marketing' || marketingOptIn;
          const smsAllowedByPurpose = emailAllowedByPurpose;
          const defaultEmail = (g.defaultSelectedChannels?.email ?? true) && eActive && emailAllowedByPurpose;
          const defaultSms = (g.defaultSelectedChannels?.sms ?? true) && sActive && smsAllowedByPurpose;
          const prevRow = next[r.id];
          const row = {
            email: eActive && emailAllowedByPurpose ? (prevRow?.email ?? defaultEmail) : false,
            sms: sActive && smsAllowedByPurpose ? (prevRow?.sms ?? defaultSms) : false,
          };
          if (!prevRow || prevRow.email !== row.email || prevRow.sms !== row.sms) {
            changed = true;
            next[r.id] = row;
          }
        }
        if (!changed) {
          return {};
        }
        return { selectedByRecipient: next };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, groups, groupStates, purpose, allowedChannels.email, allowedChannels.sms]);

  // ---------------------------------------------------------------------------
  // Notify parent of draft changes
  // ---------------------------------------------------------------------------

  // Die Rückmeldung liegt in einem Ref und nicht in den Abhängigkeiten: Fast alle
  // Assistenten übergeben `onDraftsChange` als inline erzeugte Funktion, die bei
  // jedem Rendern neu entsteht. Stand sie in den Abhängigkeiten, lief der Effekt
  // nach jedem Rendern — und weil die Aufrufer im Handler einen frischen Entwurf
  // in ihren Zustand schreiben, löste er das nächste Rendern aus. React brach
  // die Schleife mit Fehler #185 ab („Maximum update depth exceeded"), sichtbar
  // als Absturz beim Ausblenden einer Anfrage und beim Anlegen einer Probestunde
  // (T-0058, T-0059).
  const meldeEntwuerfe = useRef(onDraftsChange);
  useEffect(() => {
    meldeEntwuerfe.current = onDraftsChange;
  });

  // Was zuletzt hinausging. Der zweite Riegel: Selbst wenn der Effekt läuft,
  // weil `groups` bei jedem Rendern eine neue Kennung hat, geht nichts hinaus,
  // solange sich am Inhalt nichts geändert hat.
  const zuletztGemeldet = useRef<string | null>(null);

  useEffect(() => {
    const melde = meldeEntwuerfe.current;
    if (!active || !melde) return;

    // Solange Empfänger noch aufgelöst oder ihre Kanäle noch nicht vorbelegt
    // sind, steht die Auswahl nicht fest. Eine Meldung in diesem Moment trüge
    // `email: false` hinaus — Assistenten, die daraus ihr
    // `defaultSelectedChannels` speisen, wählten damit E-Mail dauerhaft ab
    // (Kündigung ohne Bestätigungsmail).
    const kannAufloesen = Boolean(standortId && standortId > 0);
    const offen = kannAufloesen && groups.some((g) => {
      if (!g.recipients.length) return false;
      const gs = getGS(g.key);
      if (!gs.hasResolved) return true;
      return g.recipients.some((r) => gs.resolved[r.id] && !gs.selectedByRecipient[r.id]);
    });
    if (offen) return;

    const result: Record<string, GroupDraftState> = {};
    for (const g of groups) {
      const gs = getGS(g.key);
      const emailSelected = g.recipients.some((r) => !!gs.selectedByRecipient[r.id]?.email);
      const smsSelected = g.recipients.some((r) => !!gs.selectedByRecipient[r.id]?.sms);
      result[g.key] = {
        selectedChannels: { email: emailSelected, sms: smsSelected },
        email: gs.emailDraft,
        sms: gs.smsDraft,
      };
    }

    const signatur = JSON.stringify(result);
    if (signatur === zuletztGemeldet.current) return;
    zuletztGemeldet.current = signatur;
    melde({ groups: result });
  }, [active, groups, groupStates, getGS, standortId]);

  // ---------------------------------------------------------------------------
  // Load vars when channel tab changes within a group
  // ---------------------------------------------------------------------------

  const handleChannelTabChange = useCallback(
    (groupKey: string, ch: CommunicationChannel) => {
      updateGS(groupKey, { channelTab: ch });
      const g = groups.find((grp) => grp.key === groupKey);
      if (g) void loadVarsMeta(groupKey, ch, g.templateKeys);
    },
    [groups, updateGS, loadVarsMeta],
  );

  // ---------------------------------------------------------------------------
  // Toggle recipient channel
  // ---------------------------------------------------------------------------

  const toggleRecipientChannel = useCallback(
    (groupKey: string, recipientId: string, ch: CommunicationChannel) => {
      const gs = getGS(groupKey);
      const rr = gs.resolved[recipientId];
      if (!rr?.activeChannels?.[ch]) return;
      if (purpose === 'marketing' && rr.communicationPolicy?.marketingEmailOptIn === false) return;
      updateGS(groupKey, (prev) => {
        const row = prev.selectedByRecipient[recipientId] || { email: false, sms: false };
        return {
          selectedByRecipient: {
            ...prev.selectedByRecipient,
            [recipientId]: { ...row, [ch]: !row[ch] },
          },
        };
      });
    },
    [getGS, purpose, updateGS],
  );

  // ---------------------------------------------------------------------------
  // Insert variable into editor
  // ---------------------------------------------------------------------------

  const insertVar = useCallback(
    (groupKey: string, varKey: string) => {
      const token = `{{${varKey}}}`;
      const gs = getGS(groupKey);
      if (gs.channelTab === 'email') {
        editorRefs.current[groupKey]?.insertToken(token);
        return;
      }
      updateGS(groupKey, (prev) => ({
        smsDraft: { smsTpl: (prev.smsDraft.smsTpl || '') + token },
      }));
    },
    [getGS, updateGS],
  );

  // ---------------------------------------------------------------------------
  // Render helper: single group content
  // ---------------------------------------------------------------------------

  const renderGroupContent = (g: RecipientGroup) => {
    const gs = getGS(g.key);

    return (
      <div className="space-y-4">
        {/* Recipients table */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Empfänger</div>
          <div className="text-sm text-muted-foreground">
            {g.recipients.length} Empfänger · Versand erfolgt einzeln pro Empfänger
            {gs.isResolving ? ' · Kontakte werden geladen…' : null}
          </div>

          <div className="mt-2 rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Aktiv</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.recipients.map((r) => {
                  const rr = gs.resolved[r.id];
                  const emailGesperrt = !!rr?.blockedChannels?.email;
                  const smsGesperrt = !!rr?.blockedChannels?.sms;
                  const email = emailGesperrt ? 'vom Kunden gesperrt' : (rr?.email ?? '—');
                  const phone = smsGesperrt ? 'vom Kunden gesperrt' : (rr?.phone ?? '—');
                  const eActive = allowedChannels.email !== false && !!rr?.activeChannels?.email;
                  const sActive = allowedChannels.sms !== false && !!rr?.activeChannels?.sms;
                  const eSelected = !!gs.selectedByRecipient[r.id]?.email;
                  const sSelected = !!gs.selectedByRecipient[r.id]?.sms;
                  const marketingOptIn = rr?.communicationPolicy?.marketingEmailOptIn ?? true;
                  const emailBlockedByMarketing = purpose === 'marketing' && !marketingOptIn;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{rr?.name?.trim() ? rr.name : r.name}</TableCell>
                      <TableCell className="text-muted-foreground">{String(email || '—')}</TableCell>
                      <TableCell className="text-muted-foreground">{String(phone || '—')}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {allowedChannels.sms !== false ? <button
                            type="button"
                            disabled={!eActive || emailBlockedByMarketing}
                            aria-pressed={eActive ? eSelected : undefined}
                            onClick={() => toggleRecipientChannel(g.key, r.id, 'email')}
                            className={cn(
                              'inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium transition-colors',
                              !eActive || emailBlockedByMarketing
                                ? 'cursor-not-allowed border-muted bg-muted text-muted-foreground opacity-60'
                                : eSelected
                                  ? 'cursor-pointer border-primary bg-primary text-primary-foreground'
                                  : 'cursor-pointer border-muted bg-muted/70 text-muted-foreground hover:bg-muted',
                            )}
                            title={
                              emailGesperrt
                                ? 'Der Kunde hat diese E-Mail-Adresse fürs Office gesperrt'
                                : !eActive
                                ? 'E-Mail nicht aktiv'
                                : emailBlockedByMarketing
                                  ? 'Keine Werbe-Einwilligung — werbliche Nachrichten gehen nicht an diese Person'
                                  : (eSelected ? 'E-Mail abwählen' : 'E-Mail auswählen')
                            }
                          >
                            E-Mail
                          </button> : null}
                          <button
                            type="button"
                            disabled={!sActive || emailBlockedByMarketing}
                            aria-pressed={sActive ? sSelected : undefined}
                            onClick={() => toggleRecipientChannel(g.key, r.id, 'sms')}
                            className={cn(
                              'inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium transition-colors',
                              !sActive || emailBlockedByMarketing
                                ? 'cursor-not-allowed border-muted bg-muted text-muted-foreground opacity-60'
                                : sSelected
                                  ? 'cursor-pointer border-primary bg-primary text-primary-foreground'
                                  : 'cursor-pointer border-muted bg-muted/70 text-muted-foreground hover:bg-muted',
                            )}
                            title={
                              smsGesperrt
                                ? 'Der Kunde hat diese Nummer fürs Office gesperrt'
                                : emailBlockedByMarketing
                                  ? 'Keine Werbe-Einwilligung — werbliche Nachrichten gehen nicht an diese Person'
                                  : sActive
                                  ? (sSelected ? 'SMS abwählen' : 'SMS auswählen')
                                  : 'SMS nicht aktiv'
                            }
                          >
                            SMS
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {g.recipients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">
                      Keine Empfänger
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Message editor */}
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Nachricht</div>
          </div>

          <Tabs value={gs.channelTab} onValueChange={(v) => handleChannelTabChange(g.key, v as CommunicationChannel)}>
            <TabsList>
              <TabsTrigger value="email">E-Mail</TabsTrigger>
              {allowedChannels.sms !== false ? <TabsTrigger value="sms">SMS</TabsTrigger> : null}
            </TabsList>

            <TabsContent value="email" className="mt-3 space-y-3">
              <div className="grid gap-2">
                <div className="text-sm font-medium">Betreff</div>
                <Input
                  value={gs.emailDraft.subjectTpl}
                  onChange={(e) =>
                    updateGS(g.key, (prev) => ({
                      emailDraft: { ...prev.emailDraft, subjectTpl: e.target.value },
                    }))
                  }
                  placeholder="Betreff…"
                />
              </div>
              <div className="grid gap-2">
                <div className="text-sm font-medium">Inhalt</div>
                <RichTextEditor
                  ref={(el) => {
                    editorRefs.current[g.key] = el;
                  }}
                  value={gs.emailDraft.htmlTpl}
                  onChange={(html) =>
                    updateGS(g.key, (prev) => ({
                      emailDraft: { ...prev.emailDraft, htmlTpl: html },
                    }))
                  }
                  placeholder="E-Mail Inhalt…"
                />
              </div>
            </TabsContent>

            {allowedChannels.sms !== false ? <TabsContent value="sms" className="mt-3 space-y-2">
              <div className="text-sm font-medium">SMS Text</div>
              <Textarea
                rows={7}
                value={gs.smsDraft.smsTpl}
                onChange={(e) => updateGS(g.key, { smsDraft: { smsTpl: e.target.value } })}
                placeholder="Hallo …"
              />
            </TabsContent> : null}
          </Tabs>

          <div className="mt-3">
            <NachrichtVorschau
              standortId={standortId}
              purpose={purpose}
              kanal={gs.channelTab}
              empfaenger={g.recipients.map((r, i) => ({
                id: r.id,
                name: gs.resolved[r.id]?.name?.trim() || r.name,
                backend: toBackend(g.recipients)[i],
              }))}
              entwurf={
                gs.channelTab === 'email'
                  ? {
                      subjectTpl: gs.emailDraft.subjectTpl,
                      htmlTpl: gs.emailDraft.htmlTpl,
                      textTpl: gs.emailDraft.textTpl,
                    }
                  : { smsTpl: gs.smsDraft.smsTpl }
              }
              vars={g.vars}
            />
          </div>

          {/* Variable badges */}
          {gs.allowedVarsMeta.length > 0 ? (
            <div className="mt-3 rounded-md border p-3 text-sm">
              <div className="mb-2 font-medium">Variablen</div>
              <div className="mb-2 text-xs text-muted-foreground">Klicken, um die Variable einzufügen.</div>
              <div className="flex flex-wrap gap-2">
                {gs.allowedVarsMeta.map((v) => (
                  <button key={v.key} type="button" onClick={() => insertVar(g.key, v.key)} className="cursor-pointer">
                    <Badge variant="secondary">{v.label || v.key}</Badge>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!groups.length) return null;

  // Single group → no outer tabs
  if (groups.length === 1) {
    return renderGroupContent(groups[0]);
  }

  // Multiple groups → outer tabs
  return (
    <Tabs value={activeGroupKey} onValueChange={setActiveGroupKey}>
      <TabsList className="h-11 rounded-lg bg-muted/60 p-1">
        {groups.map((g) => (
          <TabsTrigger
            key={g.key}
            value={g.key}
            className="px-5 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            {g.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {groups.map((g) => (
        <TabsContent key={g.key} value={g.key} className="mt-4">
          {renderGroupContent(g)}
        </TabsContent>
      ))}
    </Tabs>
  );
}

